import {
  Clubhouse_LeagueV2Status,
  Clubhouse_LeagueWizardScheduleMode,
} from "../../enums";
import type {
  Clubhouse_LeagueOperatorDashboardLeagueDto,
  Clubhouse_LeagueOperatorDashboardRoundScheduleDto,
  Clubhouse_LeagueOperatorDashboardStatusSummaryDto,
} from "../../types/leagues";
import { DateTime } from "luxon";
import { facilityBoundaryDate, hasPlayWindowEnded } from "../helpers";

export type DashboardView = "IN_SEASON" | "UPCOMING" | "FINISHED" | "EXCLUDED";

type StatusSummaryForClassification = Pick<
  Clubhouse_LeagueOperatorDashboardStatusSummaryDto,
  "roundsFinalized" | "totalRounds"
>;

/**
 * Buckets a league into the Leagues Dashboard's three views. Independent of
 * the older `getLeagueLifecycle` helper (helpers/index.ts), which does not
 * factor in startDateUtc and is still used by other surfaces.
 */
export function classifyLeagueView(
  status: Clubhouse_LeagueV2Status | undefined,
  startDateUtc: string | undefined,
  statusSummary: StatusSummaryForClassification | undefined,
  now: Date,
  endDateUtc?: string,
  timeZone?: string,
): DashboardView {
  if (status === Clubhouse_LeagueV2Status.ARCHIVED) return "EXCLUDED";
  if (status === Clubhouse_LeagueV2Status.DRAFT) return "UPCOMING";

  // ACTIVE, or missing status (treated as active, best-effort, matching
  // getLeagueLifecycle's existing convention).
  const started = startDateUtc != null && new Date(startDateUtc) <= now;
  if (!started) return "UPCOMING";

  // The season date window has closed. Rounds can still be stored as Open or
  // In Progress; that must not keep the league playable or "in season".
  if (
    hasPlayWindowEnded(undefined, {
      isFlex: true,
      leagueEndUtc: endDateUtc,
      schedulingTimezone: timeZone,
      now: DateTime.fromJSDate(now, { zone: "utc" }),
    })
  ) {
    return "FINISHED";
  }

  const total = statusSummary?.totalRounds ?? 0;
  const finalized = statusSummary?.roundsFinalized ?? 0;
  if (total > 0 && finalized >= total) return "FINISHED";
  return "IN_SEASON";
}

export type ProgressPercent = { finalizedPct: number; currentPct: number };

/**
 * Green-fill width (finalized rounds) plus an extra gold segment representing
 * the currently open round, both as percentages of totalRounds. Works for any
 * round count (checked at 4/8/16/20) and never divides by zero.
 */
export function progressPercent(
  roundsFinalized: number,
  totalRounds: number,
  hasOpenRound: boolean,
): ProgressPercent {
  if (totalRounds <= 0) return { finalizedPct: 0, currentPct: 0 };
  const perRound = 100 / totalRounds;
  const finalizedPct = Math.min(100, roundsFinalized * perRound);
  const currentPct = hasOpenRound ? Math.min(perRound, 100 - finalizedPct) : 0;
  return { finalizedPct, currentPct };
}

/**
 * Round whose play window has closed but which has not been finalized yet.
 *
 * `roundSchedule` has no field for one. It carries the open round, the next
 * Scheduled round and the last Finalized round, and a Completed round is none
 * of those — so it is read out of the gap they leave. With nothing open,
 * every round below `nextRoundNumber` has already been played, so a round
 * above `previousRoundNumber` (the highest Finalized) is sitting closed. With
 * no Scheduled round left either, `currentRoundNumber` is by its own
 * documented rules the highest round number, which is that same round.
 *
 * The one case this reads wrong is a Cancelled round in the gap, which it
 * would report as awaiting finalization. An explicit `completedRoundNumber`
 * on the dashboard DTO would retire the inference.
 *
 * `statusSummary.roundsReadyToFinalize` is deliberately NOT the gate here.
 * It carries no description in swagger, and the API's own finalizability DTO
 * defines ready as clean (no blocking reasons, no missing scorecards) rather
 * than merely closed. If it means "clean", gating on it would hide this line
 * for the rounds stuck on missing cards — the ones an operator most needs to
 * see. Confirm it against a closed round with missing scorecards before
 * using it; if it counts Completed rounds, it replaces the arithmetic below.
 */
export function findAwaitingFinalizationRound(
  roundSchedule: Clubhouse_LeagueOperatorDashboardRoundScheduleDto | undefined,
  statusSummary: Clubhouse_LeagueOperatorDashboardStatusSummaryDto | undefined,
): number | undefined {
  if (roundSchedule?.openRoundNumber != null) return undefined;

  const previous = roundSchedule?.previousRoundNumber ?? 0;
  const next = roundSchedule?.nextRoundNumber;
  if (next != null) return next - 1 > previous ? next - 1 : undefined;

  const total = statusSummary?.totalRounds ?? 0;
  const finalized = statusSummary?.roundsFinalized ?? 0;
  if (total > 0 && finalized < total) {
    return statusSummary?.currentRoundNumber ?? undefined;
  }
  return undefined;
}

export type StatusLine = {
  dot: "gold" | "green" | "gray";
  line1: string;
  line2: string | null;
};

/**
 * boundary must match whether `iso` is semantically a start or end instant
 * (a season start date, a round's open date vs. a round's close/finalize
 * date) — facilityBoundaryDate uses it to disambiguate a legacy
 * exact-midnight-UTC marker from a real zoned instant, so passing the wrong
 * boundary for an exact-midnight start date would show the UTC calendar day
 * instead of the facility-local one.
 */
function formatDateTime(
  iso: string | undefined,
  boundary: "start" | "end",
  tz: string | undefined,
): string {
  const dt = facilityBoundaryDate(iso, boundary, tz);
  return dt?.toFormat("ccc, MMM d") ?? "—";
}

function formatDateTimeWithTime(
  iso: string | undefined,
  boundary: "start" | "end",
  tz: string | undefined,
): string {
  const dt = facilityBoundaryDate(iso, boundary, tz);
  return dt ? `${dt.toFormat("ccc, MMM d")} at ${dt.toFormat("h:mm a")}` : "—";
}

function formatShortDate(
  iso: string | undefined,
  boundary: "start" | "end",
  tz: string | undefined,
): string {
  const dt = facilityBoundaryDate(iso, boundary, tz);
  return dt?.toFormat("MMM d") ?? "—";
}

/**
 * The status line for one league row, read entirely off the dashboard
 * payload's `roundSchedule` and `statusSummary`.
 *
 * This used to take the league's full round list, which the dashboard had to
 * fetch one request per league to get. `roundSchedule` carries the same three
 * windows the line needs — the open round's close, the next round's open, and
 * the last finalization — so the fan-out is gone.
 */
export function formatStatusLine(
  league: DashboardLeague,
  view: DashboardView,
): StatusLine {
  const { status, scheduleMode, roundSchedule, statusSummary } = league;
  const tz = league.schedulingTimezone;
  const startDateUtc = isoOf(league.startDateUtc);

  if (view === "UPCOMING") {
    const startsLine = `Starts ${formatDateTime(startDateUtc, "start", tz)}`;
    if (status === Clubhouse_LeagueV2Status.DRAFT) {
      const startDatePassed =
        startDateUtc != null && new Date(startDateUtc) <= new Date();
      return startDatePassed
        ? {
            dot: "gray",
            line1: "Draft",
            line2: `Start date has passed (${formatShortDate(startDateUtc, "start", tz)})`,
          }
        : { dot: "gray", line1: "Draft", line2: startsLine };
    }
    return { dot: "green", line1: "Finalized", line2: startsLine };
  }

  if (view === "FINISHED") {
    if (roundSchedule?.lastRoundFinalizedUtc == null) {
      return {
        dot: "gray",
        line1: "Season ended",
        line2: "Not playable",
      };
    }
    return {
      dot: "green",
      line1: `Finished ${formatShortDate(roundSchedule?.lastRoundFinalizedUtc, "end", tz)}`,
      line2: "Standings final",
    };
  }

  // IN_SEASON
  const isFlex = scheduleMode === Clubhouse_LeagueWizardScheduleMode.SeasonFlex;

  if (roundSchedule?.openRoundNumber != null) {
    // A flex round has no deadline of its own worth showing: players go at
    // their own pace and the real cutoff is the end of the season. A fixed
    // round is the opposite — its close is the date that matters, down to
    // the time of day.
    return {
      dot: "gold",
      line1: `Round ${roundSchedule.openRoundNumber} is open`,
      line2: isFlex
        ? `Flex: play by ${formatDateTime(isoOf(league.endDateUtc), "end", tz)}`
        : `Closes ${formatDateTimeWithTime(roundSchedule.openRoundClosesUtc, "end", tz)}`,
    };
  }

  // A round whose play window has closed but has not been finalized yet.
  // Checked before the between-rounds / not-open-yet branches so it is not
  // misreported as "Round n opens ..." for some other round.
  const awaiting = findAwaitingFinalizationRound(roundSchedule, statusSummary);
  if (awaiting != null) {
    return {
      dot: "green",
      line1: `Round ${awaiting} closed`,
      line2: "Awaiting finalization",
    };
  }

  const next = roundSchedule?.nextRoundNumber;
  const previous = roundSchedule?.previousRoundNumber;
  if (next != null && previous != null) {
    // `openRoundNumber` is the backend's round status (Open/InProgress), and a
    // flex round only leaves Scheduled once somebody plays it — it has no play
    // window to open it on a date. So a flex league between rounds reports a
    // Scheduled next round with no open date, which read as "opens —". With
    // the previous round finalized and the whole season as its window, that
    // round is playable now, so the line says so rather than naming a date
    // that does not exist. A flex round that carries a real open date, and
    // every fixed round, keep the dated wording.
    if (isFlex && roundSchedule?.nextRoundOpensUtc == null) {
      return {
        dot: "gold",
        line1: `Round ${next} is open`,
        line2: `Flex: play by ${formatDateTime(isoOf(league.endDateUtc), "end", tz)}`,
      };
    }
    return {
      dot: "green",
      line1: `Round ${next} opens ${formatDateTime(roundSchedule?.nextRoundOpensUtc, "start", tz)}`,
      line2: `Round ${previous} finalized ${formatShortDate(roundSchedule?.previousRoundFinalizedUtc, "end", tz)}`,
    };
  }

  // First round not open yet: no finalized round exists. Uses the round's
  // real number; 1 only when there is no round to read one from.
  //
  // A flex round carries no open date of its own — the whole season is its
  // window — so the first round's line would read "opens —". The season start
  // is the date it actually opens, so it stands in. Later rounds never reach
  // here: one of them is always open or finalized once the season is running.
  const opensUtc =
    roundSchedule?.nextRoundOpensUtc ?? (isFlex ? startDateUtc : undefined);
  return {
    dot: "green",
    line1: `Round ${next ?? statusSummary?.currentRoundNumber ?? 1} opens ${formatDateTime(opensUtc, "start", tz)}`,
    line2: `Season started ${formatShortDate(startDateUtc, "start", tz)}`,
  };
}

export type DashboardLeague = Clubhouse_LeagueOperatorDashboardLeagueDto;

/**
 * The dashboard DTO types its dates as `Date`, but the payload is JSON and
 * they arrive as ISO strings. Everything downstream parses them as strings.
 */
function isoOf(value: Date | string | undefined): string | undefined {
  return value as unknown as string | undefined;
}

function toTime(iso: string | undefined): number {
  if (!iso) return Number.POSITIVE_INFINITY;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? Number.POSITIVE_INFINITY : t;
}

export function compareUpcoming(
  a: DashboardLeague,
  b: DashboardLeague,
): number {
  return toTime(isoOf(a.startDateUtc)) - toTime(isoOf(b.startDateUtc));
}

export function compareInSeason(
  a: DashboardLeague,
  b: DashboardLeague,
): number {
  const openA = a.roundSchedule?.openRoundNumber != null;
  const openB = b.roundSchedule?.openRoundNumber != null;
  if (openA && !openB) return -1;
  if (!openA && openB) return 1;
  if (openA && openB) {
    return (
      toTime(a.roundSchedule?.openRoundClosesUtc) -
      toTime(b.roundSchedule?.openRoundClosesUtc)
    );
  }
  return (
    toTime(a.roundSchedule?.nextRoundOpensUtc) -
    toTime(b.roundSchedule?.nextRoundOpensUtc)
  );
}

export function compareFinished(
  a: DashboardLeague,
  b: DashboardLeague,
): number {
  // Descending — most recent first.
  return (
    toTime(b.roundSchedule?.lastRoundFinalizedUtc) -
    toTime(a.roundSchedule?.lastRoundFinalizedUtc)
  );
}

export type PageListItem = number | "ellipsis";

/**
 * 1, 2, ..., current, ..., last once there are 7+ pages (per the spec); every
 * page listed below that. Never emits two ellipses in a row or an ellipsis
 * adjacent to the number it would replace.
 */
export function buildPageList(
  currentPage: number,
  totalPages: number,
): PageListItem[] {
  if (totalPages <= 6) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const pages = new Set<number>([1, currentPage, totalPages]);

  // Add page to avoid isolated numbers next to ellipsis at the start
  if (currentPage > 2 && currentPage < totalPages - 1) {
    // Middle pages: include 2
    pages.add(2);
  } else if (currentPage === 2) {
    // Near start: include 3 to buffer
    pages.add(3);
  }

  // Add page to avoid isolated numbers next to ellipsis at the end
  if (currentPage === totalPages || currentPage === totalPages - 1) {
    // At or near end: include totalPages-1
    pages.add(totalPages - 1);
  }

  const sorted = Array.from(pages)
    .filter((p) => p >= 1 && p <= totalPages)
    .sort((a, b) => a - b);

  const result: PageListItem[] = [];
  let previous: number | undefined;
  for (const p of sorted) {
    if (previous !== undefined && p - previous > 1) {
      result.push("ellipsis");
    }
    result.push(p);
    previous = p;
  }
  return result;
}
